import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef } from 'react';
import * as Cesium from 'cesium';
import CircleControl from './shared/CircleControl';
// Lightweight 3D compass that tracks camera heading/pitch/roll.
// - Uses scene.postRender to update DOM transforms on the needle (no React re-renders)
// - Rendered as a CircleControl puck so it shares the forest-glass look AND the
//   hover-expand label behaviour of the top-right Map Options / Help controls.
const Compass3D = ({ viewer, inline = false }) => {
    const needleRef = useRef(null);
    const containerRef = useRef(null);
    const handleOrientNorth = useCallback(() => {
        if (!viewer || viewer.isDestroyed())
            return;
        try {
            viewer.camera.setView({
                orientation: {
                    heading: 0.0,
                    pitch: Cesium.Math.toRadians(-89.5), // near-nadir, avoid singularity
                    roll: 0.0,
                },
            });
        }
        catch (_e) {
            // no-op
        }
    }, [viewer]);
    useEffect(() => {
        if (!viewer || viewer.isDestroyed())
            return;
        const needle = needleRef.current;
        const container = containerRef.current;
        if (!needle || !container)
            return;
        // Give a subtle perspective for 3D tilt
        container.style.perspective = '360px';
        container.style.perspectiveOrigin = '50% 50%';
        const scene = viewer.scene;
        const onPostRender = () => {
            // Camera orientation in radians
            const heading = viewer.camera.heading;
            const pitch = viewer.camera.pitch;
            const roll = viewer.camera.roll;
            // Convert to degrees for CSS transforms
            const headingDeg = Cesium.Math.toDegrees(heading);
            const rollDeg = Cesium.Math.toDegrees(roll);
            // Rotate the needle so it points toward geographic north on screen.
            // Compensate for camera roll so the needle stays correct if the view is rolled.
            const rotationZDeg = -(headingDeg + rollDeg);
            // Subtle 3D tilt derived from pitch (clamped and scaled)
            const pitchClamped = Cesium.Math.clamp(pitch, Cesium.Math.toRadians(-60), Cesium.Math.toRadians(60));
            const tiltDeg = Cesium.Math.toDegrees(pitchClamped) * 0.6; // soften the tilt
            needle.style.transform = `translateZ(0) rotateX(${tiltDeg.toFixed(2)}deg) rotateZ(${rotationZDeg.toFixed(2)}deg)`;
        };
        scene.postRender.addEventListener(onPostRender);
        return () => {
            try {
                scene.postRender.removeEventListener(onPostRender);
            }
            catch (_e) {
                // no-op; viewer may be torn down during unmount
            }
        };
    }, [viewer]);
    if (!viewer)
        return null;
    // The live, camera-tracking needle doubles as the puck's resting icon.
    const needle = (_jsx("div", { ref: containerRef, className: "relative w-12 h-12", children: _jsx("div", { ref: needleRef, className: "absolute inset-0 flex items-center justify-center", style: { transformOrigin: '50% 50%' }, children: _jsxs("svg", { width: "44", height: "44", viewBox: "0 0 44 44", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M22 7 L26 22 L18 22 Z", style: { fill: 'var(--rust)' } }), _jsx("path", { d: "M22 37 L26 22 L18 22 Z", style: { fill: 'var(--dew)', fillOpacity: 0.6 } }), _jsx("circle", { cx: "22", cy: "22", r: "2.6", style: { fill: 'var(--sand)', fillOpacity: 0.9 } }), _jsx("circle", { cx: "22", cy: "22", r: "1.2", style: { fill: 'rgba(24,24,24,0.85)' } })] }) }) }));
    return (_jsx(CircleControl, { icon: needle, label: "Face north", onClick: handleOrientNorth, title: "Orient north", className: inline ? 'relative z-20' : 'absolute bottom-3 right-3 z-20' }));
};
export default Compass3D;
